// Libs
import clsx from "clsx";
import type React from "react";

// Utils
import { type RDivProps, filterProps } from "../../core/utils";

// Components
import { ControlLabel, type ControlLabelPosition, type ControlLabelProps } from "./ControlLabel";
import { FieldHoc } from "./Field";
import type { InputProps } from "./Input";

export interface RadioProps {
    /**
     * If radio is checked
     */
    checked?: boolean;
    /**
     * Additional className for circle
     */
    circleClassName?: string;
    /**
     * Additional props for circle
     */
    circleProps?: RDivProps;
    /**
     * Additional className for the radio
     */
    className?: string;
    /**
     * If disabled
     */
    disabled?: boolean;
    /**
     * If there has an error
     */
    hasError?: boolean;
    /**
     * Id for description element
     */
    id?: string;
    /**
     * Additional className for input
     */
    inputClassName?: string;
    /**
     * Additional props for input
     */
    inputProps?: InputProps;
    /**
     * Label for radio
     */
    label?: React.ReactNode;
    /**
     * Optional sub label / subtitle displayed under the main label
     */
    subLabel?: React.ReactNode;
    /**
     * Additional className for label
     */
    labelClassName?: string;
    /**
     * Position for label
     */
    labelPosition?: ControlLabelPosition;
    /**
     * Additional props for label
     */
    labelProps?: ControlLabelProps;
    /**
     * Name of the input
     */
    name: string;
    /**
     * Change handler
     */
    onChange: React.ChangeEventHandler<HTMLInputElement>;
    /**
     * Click handler
     */
    onClick?: React.MouseEventHandler<HTMLDivElement>;
    /**
     * Blur handler
     */
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
    /**
     * Focus handler
     */
    onFocus?: React.FocusEventHandler<HTMLInputElement>;
    /**
     * If is required
     */
    required?: boolean;
    /**
     * Additional className for thumb
     */
    thumbClassName?: string;
    /**
     * Additional props for thumb
     */
    thumbProps?: RDivProps;
    /**
     * Value of the input
     */
    value?: number | string | undefined;
}

export function Radio({
    checked = false,
    circleClassName: circleClassNameProp,
    circleProps,
    className,
    disabled = false,
    hasError = false,
    id,
    inputClassName: inputClassNameProp,
    inputProps,
    label,
    subLabel,
    labelClassName: labelClassNameProp,
    labelProps,
    labelPosition,
    name,
    onClick,
    onChange,
    onFocus,
    onBlur,
    required = false,
    thumbClassName: thumbClassNameProp,
    thumbProps,
    value,
    ...otherProps
}: RadioProps): JSX.Element {
    const rootClassName = clsx(
        "ds-c-radio",
        {
            "ds-c-radio--error": hasError && !disabled,
            "ds-c-radio--checked": checked,
        },
        className
    );

    const inputClassName = clsx("ds-c-radio__input", inputClassNameProp);
    const circleClassName = clsx("ds-c-radio__circle", circleClassNameProp);
    const thumbClassName = clsx("ds-c-radio__thumb", thumbClassNameProp);
    const labelClassName = clsx("ds-c-radio__label", labelClassNameProp);
    // Compose label content so we can optionally render a subtitle under the main label
    const composedLabel = (
        <div className="ds-o-flex ds-o-flex--align-start ds-o-flex--column">
            {label}
            {subLabel && <div className="ds-c-radio__sublabel">{subLabel}</div>}
        </div>
    );

    return (
        <ControlLabel
            {...labelProps}
            disabled={disabled}
            hasError={hasError}
            id={id}
            label={composedLabel}
            className={labelClassName}
            labelPosition={labelPosition}
        >
            <div
                {...filterProps(otherProps)}
                className={rootClassName}
                disabled={disabled === true}
            >
                <input
                    {...filterProps(inputProps)}
                    checked={checked}
                    className={inputClassName}
                    disabled={disabled}
                    id={id}
                    name={name}
                    onClick={onClick}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onChange={onChange}
                    required={required}
                    type="radio"
                    value={value}
                />
                <div {...filterProps(circleProps)} className={circleClassName}>
                    <span {...filterProps(thumbProps)} className={thumbClassName} />
                </div>
            </div>
        </ControlLabel>
    );
}

export const RadioField = FieldHoc(Radio, "Radio");
